import { useState } from 'react';
import { ApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { useFormFactor } from '@/lib/formFactor';
import { deleteRole, updateAccount } from './api';
import { effectivePerms, PERM_LABEL, summarizeRole } from './permissions';
import type { Role, UserRow } from './types';
import s from './RoleDeleteFlow.module.css';

const READONLY = import.meta.env.VITE_READONLY !== '0';

export function RoleDeleteFlow({ role, roles, members, canMigrate, onClose, onAccountsChanged, onDeleted }: {
  role: Role;
  roles: Role[];
  members: UserRow[];
  canMigrate: boolean;
  onClose: () => void;
  onAccountsChanged: () => void;
  onDeleted: () => void;
}) {
  const ff = useFormFactor();
  const options = roles.filter((item) => item.isActive && item.id !== role.id);
  const defaultTarget = options.find((item) => item.code === 'VIEWER') || options[0];
  const preferredTargets = ['VIEWER', 'PURCHASE', 'SALES_LEAD']
    .map((code) => options.find((item) => item.code === code))
    .filter((item): item is Role => !!item);
  const otherTargets = options.filter((item) => !preferredTargets.some((preferred) => preferred.id === item.id));
  const [targets, setTargets] = useState<Record<string, string>>(() => Object.fromEntries(members.map((user) => [user.id, defaultTarget?.code || ''])));
  const [pendingIds, setPendingIds] = useState(() => members.map((item) => item.id));
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [showOtherTargets, setShowOtherTargets] = useState(false);
  const [migrated, setMigrated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const inUse = role.userCount > 0 && !migrated;
  const targetFor = (userId: string) => options.find((item) => item.code === (targets[userId] || options[0]?.code));
  const permissionLoss = (target: Role) => [...effectivePerms(role)]
    .filter((code) => !effectivePerms(target).has(code))
    .map((code) => PERM_LABEL[code] || code)
    .slice(0, 5);

  const migrate = async () => {
    if (!pendingIds.length || pendingIds.some((id) => !targetFor(id)) || !canMigrate || READONLY || busy || uncertain) return;
    setBusy(true); setError('');
    let remaining = [...pendingIds];
    try {
      for (const id of pendingIds) {
        const target = targetFor(id);
        if (!target) throw new Error('请为每个账号选择目标角色。');
        await updateAccount(id, { role: target.code });
        remaining = remaining.filter((item) => item !== id);
        setPendingIds(remaining);
      }
      setMigrated(true);
      onAccountsChanged();
    } catch (cause) {
      onAccountsChanged();
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(`迁移中断，已有 ${members.length - remaining.length} 个账号改为新角色；${cause instanceof Error ? cause.message : '请核对账号列表'}`);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (role.isPreset || inUse || READONLY || busy || uncertain) return;
    setBusy(true); setError('');
    try {
      await deleteRole(role.id);
      onDeleted();
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '删除失败');
    } finally { setBusy(false); }
  };

  if (role.isPreset) return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className={s.dialog} role="dialog" aria-modal="true" aria-label={`删除角色${role.name}`}>
      <header><h2>删除角色「{role.name}」？</h2><button type="button" onClick={onClose} aria-label="关闭">×</button></header>
      <div className={s.body}><p className={s.danger}>预置角色不能删除。此角色可在权限编辑器中调整权限。</p></div>
      <footer><button type="button" onClick={onClose}>知道了</button></footer>
    </section>
  </div>;

  if (inUse && !migrationOpen) return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className={`${s.dialog} ${s.blockedDialog}`} role="dialog" aria-modal="true" aria-label={`删除角色${role.name}`}>
      <div className={s.blockedBody}>
        <span className={s.blockedIcon}><Icon name="shield" size={22} /></span>
        <h2>删除角色「{role.name}」？</h2>
        <p>当前有 <b>{members.length || role.userCount} 个账号</b>正在使用这个角色。必须先把它改到别的角色，才能删除。</p>
        <div className={s.affected}><b>受影响的账号</b>{members.map((user) => <span key={user.id}>{user.name} · {user.username} · {user.isActive ? '启用' : '停用'}</span>)}</div>
        <p className={s.blockedHint}>「{role.name}」是自定义角色，账号迁走后可以删除。预置角色不可删除，只能停用。</p>
      </div>
      <footer className={s.blockedFoot}>
        <button type="button" onClick={onClose}>暂不删除</button>
        <button type="button" className={s.primary} disabled={!canMigrate || !members.length || READONLY} onClick={() => setMigrationOpen(true)}>迁移账号</button>
      </footer>
    </section>
  </div>;

  const finalStep = migrated;
  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <section className={`${s.dialog} ${s.migrationSheet} ${ff === 'phone' ? s.phoneSheet : ''}`} role="dialog" aria-modal="true" aria-label={migrated ? `确认删除角色${role.name}` : `迁移角色${role.name}下的账号`}>
      <header className={s.migrationHead}>
        {!finalStep && <button type="button" className={s.phoneCancel} onClick={() => { if (!busy) setMigrationOpen(false); }}>取消</button>}
        <div><h2>{finalStep ? `第 2 步 · 删除角色「${role.name}」` : '迁移账号'}</h2>{!finalStep && <small>删除「{role.name}」之前</small>}</div>
        <button type="button" onClick={() => { if (busy) return; if (!migrated) setMigrationOpen(false); else onClose(); }} disabled={busy} aria-label="关闭">×</button>
      </header>
      <div className={s.body}>
        <div className={s.steps}><span className={!finalStep ? s.activeStep : s.doneStep}><i>{finalStep ? '✓' : '1'}</i>{!finalStep ? `迁移 ${pendingIds.length} 个账号` : '迁移账号'}</span><em /><span className={finalStep ? s.activeStep : ''}><i>2</i>{ff === 'phone' ? '删除角色' : `删除角色「${role.name}」`}</span></div>
        {finalStep ? <>
          <p className={s.danger}>将删除自定义角色「{role.name}」。此操作不能撤销。</p>
          <p className={s.note}>账号已迁移。删除前，服务端会再次检查是否仍有账号在使用这个角色。</p>
        </> : <>
          <div className={s.phoneMembers}>
            {members.map((user) => {
              const selectedCode = targets[user.id] || defaultTarget?.code || '';
              return <section className={s.phoneMember} key={`phone-${user.id}`}>
                <header><b>{user.name}</b><span className="tnum">{user.username} · {user.isActive ? '启用' : '停用'}</span></header>
                <b className={s.phoneTargetLabel}>* 改到哪个角色</b>
                <div className={s.phoneTargets}>
                  {preferredTargets.map((item) => <button type="button" key={item.id} aria-pressed={selectedCode === item.code} className={selectedCode === item.code ? s.phoneTargetOn : s.phoneTarget} onClick={() => setTargets((current) => ({ ...current, [user.id]: item.code }))}>
                    <b>{item.name}</b><span>{summarizeRole(item).headline}</span>
                  </button>)}
                  {otherTargets.length > 0 && <button type="button" aria-expanded={showOtherTargets} className={s.phoneOtherToggle} onClick={() => setShowOtherTargets((value) => !value)}>{showOtherTargets ? '收起其他角色' : `其他 ${otherTargets.length} 个角色`}<span>{showOtherTargets ? '⌃' : '›'}</span></button>}
                  {showOtherTargets && otherTargets.map((item) => <button type="button" key={item.id} aria-pressed={selectedCode === item.code} className={selectedCode === item.code ? s.phoneTargetOn : s.phoneTarget} onClick={() => setTargets((current) => ({ ...current, [user.id]: item.code }))}>
                    <b>{item.name}</b><span>{summarizeRole(item).headline}</span>
                  </button>)}
                </div>
              </section>;
            })}
          </div>
          <div className={s.memberTableWrap}><table className={s.memberTable}>
            <thead><tr><th>账号</th><th>姓名</th><th>状态</th><th>改到哪个角色</th></tr></thead>
            <tbody>{members.map((user) => <tr key={user.id}>
              <td className="tnum">{user.username}</td><td>{user.name}</td><td><span className={user.isActive ? s.memberActive : s.memberInactive}>{user.isActive ? '启用' : '停用'}</span></td>
              <td><select aria-label={`${user.username}改到哪个角色`} value={targets[user.id] || options[0]?.code || ''} onChange={(event) => setTargets((current) => ({ ...current, [user.id]: event.target.value }))}>
                {options.map((item) => <option key={item.id} value={item.code}>{item.name}（{item.isPreset ? '预置' : '自定义'}）</option>)}
              </select></td>
            </tr>)}</tbody>
          </table></div>
          {members.map((user) => {
            const target = targetFor(user.id);
            if (!target) return null;
            const lost = permissionLoss(target);
            return <div className={s.preview} key={`${user.id}-${target.id}`}>
              <b>迁移后{user.name}的变化</b>
              <p>{lost.length ? `将失去 ${lost.join('、')}${effectivePerms(role).size > lost.length ? '等权限' : ''}。` : `${target.name}角色的可用权限会立即生效。`}</p>
            </div>;
          })}
          <p className={s.note}>改角色立即按新权限生效，不会让账号退出登录。迁移完成后，第 2 步再确认删除「{role.name}」。</p>
        </>}
        {READONLY && <p className={s.note}>当前为生产只读模式，可以查看流程，不能提交。</p>}
        {error && <p className={s.error} role="alert">{error}{uncertain && '。结果未确认，请刷新账号与角色后核对，暂勿重试。'}</p>}
      </div>
      <footer>
        <button type="button" className={s.footerCancel} onClick={() => { if (busy) return; if (!migrated) setMigrationOpen(false); else onClose(); }} disabled={busy}>取消</button>
        {finalStep
          ? <button type="button" className={s.destructive} disabled={READONLY || busy || uncertain} onClick={() => void remove()}>{busy ? '删除中…' : '确认删除角色'}</button>
          : <button type="button" className={s.primary} disabled={!pendingIds.length || pendingIds.some((id) => !targetFor(id)) || !canMigrate || READONLY || busy || uncertain} onClick={() => void migrate()}>{busy ? '迁移中…' : `迁移 ${pendingIds.length} 个账号`}</button>}
      </footer>
    </section>
  </div>;
}
